import React, { useState, useEffect } from 'react';
import { Clock, Calendar, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface CountdownTimerProps {
  targetDate: string; // ISO date string
  className?: string;
  showIcon?: boolean;
  variant?: 'default' | 'compact' | 'urgent' | 'card';
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({
  targetDate,
  className = '',
  showIcon = true,
  variant = 'default'
}) => {
  const { t, i18n } = useTranslation();
  const tr = (key: string, en: string, fr: string) => {
    const translated = t(key);
    if (!translated || translated === key) {
      return i18n.language === 'fr' ? fr : en;
    }
    return translated;
  };
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = new Date(targetDate).getTime() - new Date().getTime();
      
      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((difference / 1000 / 60) % 60);
        const seconds = Math.floor((difference / 1000) % 60);

        setTimeLeft({ days, hours, minutes, seconds });
        setIsExpired(false);
      } else {
        setIsExpired(true);
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  if (isExpired) {
    return (
      <div className={`inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-xl text-sm font-medium ${className}`}>
        {showIcon && <AlertCircle className="h-4 w-4" />}
        <span>{tr('timer.expired', 'Training ended', 'Formation terminée')}</span>
      </div>
    );
  }

  const getVariantStyles = () => {
    switch (variant) {
      case 'compact':
        return 'text-xs';
      case 'urgent':
        return 'text-sm font-semibold text-brand-secondary';
      case 'card':
        return 'text-sm font-medium';
      default:
        return 'text-sm';
    }
  };

  const getContainerStyles = () => {
    switch (variant) {
      case 'compact':
        return 'inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-lg';
      case 'urgent':
        return 'inline-flex items-center gap-2 px-4 py-2 bg-brand-secondary/10 text-brand-secondary border border-brand-secondary/20 rounded-xl';
      case 'card':
        return 'bg-gradient-to-r from-brand-primary to-purple-600 text-white rounded-xl p-3 sm:p-4 shadow-lg max-w-full overflow-hidden';
      default:
        return 'inline-flex items-center gap-2 px-4 py-2 bg-brand-primary/10 text-brand-primary border border-brand-primary/20 rounded-xl';
    }
  };

  const formatNumber = (num: number) => num.toString().padStart(2, '0');

  const formatTimeRemaining = () => {
    const { days, hours, minutes, seconds } = timeLeft;
    
    // Si plus d'un mois (30 jours)
    if (days > 30) {
      const months = Math.floor(days / 30);
      const remainingDays = days % 30;
      
      if (remainingDays === 0) {
        return i18n.language === 'fr'
          ? `${months} mois`
          : `${months} month${months > 1 ? 's' : ''}`;
      } else {
        if (i18n.language === 'fr') {
          return `${months} mois et ${remainingDays} jour${remainingDays > 1 ? 's' : ''}`;
        }
        return `${months} month${months > 1 ? 's' : ''} and ${remainingDays} day${remainingDays > 1 ? 's' : ''}`;
      }
    }
    
    // Si plus d'un jour
    if (days > 0) {
      if (days === 1) {
        return i18n.language === 'fr'
          ? `1 jour, ${formatNumber(hours)}h ${formatNumber(minutes)}m`
          : `1 day, ${formatNumber(hours)}h ${formatNumber(minutes)}m`;
      } else {
        return i18n.language === 'fr'
          ? `${days} jours, ${formatNumber(hours)}h ${formatNumber(minutes)}m`
          : `${days} days, ${formatNumber(hours)}h ${formatNumber(minutes)}m`;
      }
    }
    
    // Si moins d'un jour
    if (hours > 0) {
      return `${formatNumber(hours)}h ${formatNumber(minutes)}m ${formatNumber(seconds)}s`;
    }
    
    // Si moins d'une heure
    if (minutes > 0) {
      return `${formatNumber(minutes)}m ${formatNumber(seconds)}s`;
    }
    
    // Si moins d'une minute
    return `${formatNumber(seconds)}s`;
  };

  const renderCardVariant = () => {
    const { days, hours, minutes, seconds } = timeLeft;
    
    return (
      <div className={`${getContainerStyles()} ${className} w-full`}>
        <div className="flex items-center justify-center gap-2 mb-2 sm:mb-3">
          {showIcon && <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />}
          <span className="text-xs sm:text-sm font-semibold text-center">{tr('timer.startsIn', 'Starts in:', 'Formation dans :')}</span>
        </div>
        
        {/* Mobile: 2x2 grid, Desktop: 4 columns */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-2">
          <div className="text-center">
            <div className="bg-white/20 rounded-lg p-2 sm:p-2.5 mb-1.5 sm:mb-1 shadow-md border border-white/30">
              <div className="text-lg sm:text-xl md:text-2xl font-bold">{formatNumber(days)}</div>
            </div>
            <div className="text-[11px] sm:text-xs opacity-90 font-medium leading-tight">{tr('timer.days', 'Days', 'Jours')}</div>
          </div>
          
          <div className="text-center">
            <div className="bg-white/20 rounded-lg p-2 sm:p-2.5 mb-1.5 sm:mb-1 shadow-md border border-white/30">
              <div className="text-lg sm:text-xl md:text-2xl font-bold">{formatNumber(hours)}</div>
            </div>
            <div className="text-[11px] sm:text-xs opacity-90 font-medium leading-tight">{tr('timer.hours', 'Hours', 'Heures')}</div>
          </div>
          
          <div className="text-center">
            <div className="bg-white/20 rounded-lg p-2 sm:p-2.5 mb-1.5 sm:mb-1 shadow-md border border-white/30">
              <div className="text-lg sm:text-xl md:text-2xl font-bold">{formatNumber(minutes)}</div>
            </div>
            <div className="text-[11px] sm:text-xs opacity-90 font-medium leading-tight">{tr('timer.minutes', 'Minutes', 'Minutes')}</div>
          </div>
          
          <div className="text-center">
            <div className="bg-white/20 rounded-lg p-2 sm:p-2.5 mb-1.5 sm:mb-1 shadow-md border border-white/30">
              <div className="text-lg sm:text-xl md:text-2xl font-bold">{formatNumber(seconds)}</div>
            </div>
            <div className="text-[11px] sm:text-xs opacity-90 font-medium leading-tight">{tr('timer.seconds', 'Seconds', 'Secondes')}</div>
          </div>
        </div>
      </div>
    );
  };

  // Render card variant if specified
  if (variant === 'card') {
    return renderCardVariant();
  }

  return (
    <div className={`${getContainerStyles()} ${getVariantStyles()} ${className}`}>
      {showIcon && <Clock className="h-4 w-4" />}
      <span>
        {tr('timer.timeLeftPrefix', 'Time left', 'Il reste')} {formatTimeRemaining()}
      </span>
    </div>
  );
};

export default CountdownTimer;
